import { Platform } from 'react-native';

/**
 * Web-only a11y guard for the console warning:
 *
 *   "Blocked aria-hidden on an element because its descendant retained focus.
 *    The focus must not be hidden from assistive technology users."
 *
 * React Navigation hides inactive screens — and react-native-web's <Modal>
 * hides the app background — by setting `aria-hidden="true"` on a full-screen
 * container. If a Pressable inside that container held DOM focus when it was
 * hidden (e.g. the very button the user clicked to navigate or open the modal),
 * the browser blocks it: per the WAI-ARIA spec, focus must not remain inside an
 * aria-hidden subtree.
 *
 * We can't change how the navigation/modal libraries apply `aria-hidden`, so we
 * react to it: when a node becomes aria-hidden while it holds the active
 * element, blur that element (focus falls back to <body>; the library's own
 * focus management then moves it into the visible screen/modal). This is the
 * behaviour the spec asks for and matches what `inert` would do to focus.
 *
 * Native (iOS/Android) is a no-op — there is no DOM.
 */

/**
 * Pure decision: should `active` be blurred because `node` just became
 * aria-hidden? True only when the node is genuinely hidden AND actually holds
 * the focused element (is it, or contains it). Kept separate from the DOM
 * plumbing so it is unit-testable in the node jest env.
 */
export function focusTrappedInHidden(
  // `contains` typed loosely so a real DOM Element (contains: (Node|null)) and
  // test fakes both satisfy it.
  node: { getAttribute?: (n: string) => string | null; contains?: (o: any) => boolean } | null,
  active: unknown,
): boolean {
  if (!node || !active) return false;
  if (node.getAttribute?.('aria-hidden') !== 'true') return false;
  if (node === active) return true;
  return typeof node.contains === 'function' && node.contains(active);
}

let installed = false;

export function installAriaHiddenFocusGuard(): void {
  if (Platform.OS !== 'web' || installed) return;
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
  if (!document.body) return;
  installed = true;

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type !== 'attributes' || m.attributeName !== 'aria-hidden') continue;
      const active = document.activeElement as HTMLElement | null;
      // Ignore <body>/null focus — nothing meaningful to rescue.
      if (!active || active === document.body) continue;
      if (focusTrappedInHidden(m.target as Element, active)) active.blur();
    }
  });

  observer.observe(document.body, {
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden'],
  });
}
